import { TIME_UNITS } from '@federa/const/time-units';
import { TWILIO_MESSAGE_STATUS } from '@federa/const/issues-messaging';
import type { BidStatus } from '@federa/types/properties-auction';
import type { ChatChannel } from '@federa/types/conversations-chat-structs';
import type { AuctionState } from '@federa/types/api-property';

export const MESSAGE_CONSTANTS = {
	SERVICE_SMS: 'sms',
	SERVICE_WHATSAPP: 'whatsapp',
	SERVICE_WEB: 'web',
	FLOW_HANDLE_INCOMING_TWILIO: 'handleincomingtwiliomessages',
	STATUS_DELIVERED: 'delivered',
	/** The assistant's rendered DISPLAY NAME on a saved message row (`buildMessageValuesTail`'s `displayName`), never a
	 *  message TYPE -- nothing reads it as a discriminator. Equal to {@link MESSAGE_CONSTANTS.IDENTITY_ALEX} today by
	 *  coincidence, not by contract: that one is the `messages.from_identifier` IDENTITY, and the two are free to diverge. */
	DISPLAY_NAME_ALEX: 'alex',
	ROLE_AI_ASSISTANT: 'ai_assistant',
	IDENTITY_ALEX: 'alex',
} as const;

export const DEFAULT_MESSAGE_CHANNEL = 'web' as const;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the service a TWILIO inbound message arrived on -- WhatsApp when
 * the sender address carries the WhatsApp prefix, else SMS. Twilio never delivers `web`, so the two arms are the
 * whole answer. The inbound conversation input and the chat-create flow each spelled this ternary.
 */
export function twilioInboundService(
	isFromWhatsapp: boolean,
): typeof MESSAGE_CONSTANTS.SERVICE_WHATSAPP | typeof MESSAGE_CONSTANTS.SERVICE_SMS {
	return isFromWhatsapp ? MESSAGE_CONSTANTS.SERVICE_WHATSAPP : MESSAGE_CONSTANTS.SERVICE_SMS;
}

/** SSOT for the `modifying_flow` identifiers a saved message row carries, keyed by the save arm that produces it. Homed here rather than beside `getMessageFlow` (`tools/audit/message-flow-identifiers.ts`) because the KEY SET is a second consumer's contract: `MessageValuesDeps` in `tools/messaging/save-message-values.ts` types the injected lookup, and `QUAL-NO-CROSS-TOOL-IMPORTS-001` forbids the tools->tools edge that would let it reuse the type in place. */
export const MESSAGE_FLOW_NAMES = {
	storageGateway: 'storage-gateway-initial-message',
	storeMessage: 'store-message-retrieval-function-storage-gateway',
} as const;

/** The save arm selecting a `MESSAGE_FLOW_NAMES` entry. Derived, never restated as a plain union -- a new arm must reach every consumer as a type error. */
export type MessageFlowKey = keyof typeof MESSAGE_FLOW_NAMES;

/** SSOT for the conversations.channel enumeration, mirroring `ChatChannel` and the LIVE `CHECK(channel IS NULL OR channel IN (...))` clause captured in `migrations/live-schema/federa/federa-dev-live-schema.sql` and its `-prod` sibling. NO in-repo migration declares that clause -- the federa D1 (CONVERSATIONS_DB) is operator-managed and sits outside the repo migration tracker, so the live-schema exports are the only in-tree witness (the earlier attribution to "migration 0006" was false: that leaf drops legacy tables and names no channel). Runtime callers must reference these constants -- never inline the literals -- so column-value drift surfaces as a TypeScript error. */
export const CHAT_CHANNELS = {
	CHAT: 'chat',
	SMS: 'sms',
	WHATSAPP: 'whatsapp',
	NOTIFICATION: 'notification',
	EMAIL: 'email',
	PHONE: 'phone',
} as const satisfies Record<string, ChatChannel>;

/** Exhaustive runtime-iterable list of valid `ChatChannel` values (matches the type union). */
export const VALID_CHAT_CHANNELS = [
	CHAT_CHANNELS.CHAT,
	CHAT_CHANNELS.SMS,
	CHAT_CHANNELS.WHATSAPP,
	CHAT_CHANNELS.NOTIFICATION,
	CHAT_CHANNELS.EMAIL,
	CHAT_CHANNELS.PHONE,
] as const satisfies readonly ChatChannel[];

/**
 * The channels a USER-REACHABLE conversation-CREATION flow input may admit: `VALID_CHAT_CHANNELS` minus
 * {@link CHAT_CHANNELS.PHONE}. Derived by runtime filter, never re-typed -- a new `CHAT_CHANNELS` member joins this
 * list automatically, and excluding `phone` stays the ONE decision spelled here.
 *
 * ADJUDICATION (violation-program task #32): admitting `phone` at a creation-flow input was the root defect. A row
 * written with `channel = 'phone'` renders on the Connect desk as a CALL, not a chat -- `conversationStream`'s
 * `kind` derivation maps `phone` to `'call'`, and `ConversationView` gates its reply box (and the AI toggle) on
 * `kind === 'text'` -- so a user-created `phone` conversation is permanently unanswerable. `phone` rows are minted
 * by the voice path (`connect-call-do`), which does not go through these flow inputs. Live `federa-dev` at
 * adjudication time held ZERO `phone` conversations (channels present: NULL, `chat`, `sms`), so the narrowing
 * strands no existing row.
 *
 * Note this gates the flow INPUT contract only; the column itself keeps the wider live CHECK, because the voice
 * path legitimately writes `phone`.
 */
export const USER_CREATABLE_CHAT_CHANNELS = VALID_CHAT_CHANNELS.filter(
	(channel): channel is Exclude<ChatChannel, typeof CHAT_CHANNELS.PHONE> => channel !== CHAT_CHANNELS.PHONE,
);

/** Twilio status polling interval in ms (used by WhatsApp delivery check). */
export const TWILIO_STATUS_POLL_INTERVAL_MS = 4000;

export const TWILIO_STATUS_MAX_ATTEMPTS = 6;

/** In-repo synthetic sentinel returned by `pollStatusOnce` / `checkMessageStatus` when the Twilio status fetch fails internally. Producers may emit `<sentinel>: <detail>`, so consumers compare via prefix or strict equality. */
export const POLL_INTERNAL_ERROR_SENTINEL = 'internal-error' as const;

/** Twilio message statuses meaning the message was NOT delivered, read off the platform vocabulary
 *  (`TWILIO_MESSAGE_STATUS`, `const/issues-messaging.ts`) rather than respelled. Includes the synthetic
 *  `POLL_INTERNAL_ERROR_SENTINEL` so the polling fallback is treated as a failure rather than slipping through as
 *  non-failure. */
export const FAILURE_STATUSES: ReadonlySet<string> = new Set([
	TWILIO_MESSAGE_STATUS.FAILED,
	TWILIO_MESSAGE_STATUS.UNDELIVERED,
	POLL_INTERNAL_ERROR_SENTINEL,
]);

/** Single decision point for the auction extension window; EXTENSION_SECONDS and EXTENSION_MS below both derive from this literal. */
const AUCTION_EXTENSION_SECONDS = 30;

export const AUCTION_CONFIG = {
	MINIMUM_BID_AMOUNT: 5000,
	BID_INCREMENT: 100,
	EXTENSION_SECONDS: AUCTION_EXTENSION_SECONDS,
	EXTENSION_MS: AUCTION_EXTENSION_SECONDS * TIME_UNITS.SECOND_MS,
	/** How far past the previous end a late-firing alarm is still accepted and the extension re-anchored from now(). Late fires happen when CF auto-retries the alarm and cumulative backoff carries the fire time past the previous end. */
	EXTENSION_GRACE_MS: 5 * TIME_UNITS.MINUTE_MS,
	COMPETING_BIDDER_ROLE: 'g4',
	/** Interchange fee on competing bids -- nullified to 0 so the competing bid equals the original amount (was 0.03). The markup machinery is intentionally preserved; restore to 0.03 to re-enable. */
	COMPETING_BID_MARKUP: 0,
	/** Flat buffer added on top of a competing bid -- nullified to 0 so the competing bid equals the original amount (was 1000). Restore to 1000 to re-enable. */
	COMPETING_BID_FIXED_AMOUNT: 0,
	/** Minimum account balance required to place an auction bid */
	MINIMUM_BALANCE: 5000,
	/** Minimum acceptable bid amount regardless of other bids */
	MINIMUM_BID_FLOOR: 5000,
	/** Threshold above the max offer that triggers confirmation */
	LARGE_BID_THRESHOLD: 100000,
	/** Minutes to extend auction on a valid last-second bid */
	EXTENSION_MINUTES: 2,
} as const;

export const VALID_BID_STATUSES = ['active', 'accepted', 'rejected', 'withdrawn'] as const satisfies readonly BidStatus[];

/** 24 hours in seconds -- threshold for auction countdown urgency indicators */
export const AUCTION_URGENCY_THRESHOLD_SECONDS = TIME_UNITS.DAY_S;

export const AUCTION_EXTENSION_NOTIFICATION_DURATION_MS = 6000;

export const AUCTION_STATE = {
	INACTIVE: 'inactive',
	ACTIVE: 'active',
	ENDED: 'ended',
	WON: 'won',
	LOST: 'lost',
	CANCELED: 'canceled',
	GOING_ONCE: 'goingonce',
	FINISHED: 'finished',
} as const satisfies Record<string, AuctionState>;

export const STOP_LOSS_FACTOR = 0.97;

/** Trigger identifiers passed to voidPreviousOffers / finalizeBidPostProcessing. */
export const VOID_TRIGGERS = {
	NEW_BID: 'new-bid-trigger',
	NEW_BID_COMPETING: 'new-bid-trigger-competing',
	AUCTION_WON: 'auction-won-trigger',
	AUCTION_LOST: 'auction-lost-trigger',
	AUCTION_CANCELED: 'auction-canceled-trigger',
} as const;
